import React, { useState } from 'react';
import { CheckIcon } from '../icons';

const CATEGORY_ORDER = ['Core', 'Deals', 'Catalog', 'Reference', 'System'];

const TYPE_ICONS = {
  string: { symbol: 'Aa', color: 'text-blue-500' },
  number: { symbol: '#', color: 'text-green-600' },
  date: { symbol: '\u29D6', color: 'text-amber-500' },
  enum: { symbol: '\u2261', color: 'text-purple-500' },
};

function groupByCategory(schema) {
  const groups = {};
  for (const rt of schema) {
    const cat = rt.category || 'Core';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(rt);
  }
  return CATEGORY_ORDER.filter(c => groups[c]).map(c => ({ category: c, types: groups[c] }));
}

export default function SchemaCatalog({
  schema,
  selectedReportType,
  onSelectReportType,
  selectedColumns,
  onToggleColumn,
  mode,
}) {
  const [expandedCategories, setExpandedCategories] = useState(new Set(['Core']));
  const [expandedTables, setExpandedTables] = useState(new Set());

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });
  };

  const toggleTable = (key) => {
    setExpandedTables(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleSelectTable = (key) => {
    onSelectReportType(key);
    setExpandedTables(prev => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  if (!schema) return null;
  const groups = groupByCategory(schema);
  const selectedSet = new Set(selectedColumns || []);
  const isTabular = mode === 'tabular';

  return (
    <div className="text-sm">
      <div className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">
        Data Sources
      </div>
      {groups.map(g => (
        <div key={g.category}>
          <button
            onClick={() => toggleCategory(g.category)}
            className="w-full flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:text-gray-700 hover:bg-gray-50"
          >
            <svg
              className={`w-3 h-3 transition-transform ${expandedCategories.has(g.category) ? 'rotate-90' : ''}`}
              fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
            {g.category}
          </button>

          {expandedCategories.has(g.category) && (
            <div className="ml-2">
              {g.types.map(rt => {
                const isSelected = selectedReportType === rt.key;
                const isExpanded = expandedTables.has(rt.key);
                const isActiveTable = isSelected && isTabular;

                return (
                  <div key={rt.key}>
                    <div className="flex items-center">
                      <button
                        onClick={() => toggleTable(rt.key)}
                        className="p-0.5 text-gray-600 hover:text-gray-600"
                      >
                        <svg
                          className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                          fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleSelectTable(rt.key)}
                        className={`flex-1 flex items-center gap-1.5 px-1.5 py-1 rounded text-left truncate ${
                          isSelected
                            ? 'bg-primary-100 text-primary-700 font-medium'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                        title={rt.description}
                      >
                        <svg className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0112 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M10.875 12h-7.5c-.621 0-1.125.504-1.125 1.125M12 12h7.5c.621 0 1.125.504 1.125 1.125" />
                        </svg>
                        <span className="truncate">{rt.label}</span>
                      </button>
                    </div>

                    {isExpanded && (
                      <div className="ml-6 border-l border-gray-200 pl-1 py-0.5">
                        {rt.columns.map(col => {
                          const icon = TYPE_ICONS[col.type] || TYPE_ICONS.string;
                          const isColSelected = isActiveTable && selectedSet.has(col.key);
                          const canClick = isTabular && onToggleColumn;

                          const handleColumnClick = () => {
                            if (!canClick) return;
                            if (!isSelected) {
                              // Switch to this table and pre-select this column
                              onSelectReportType(rt.key, col.key);
                            } else {
                              onToggleColumn(col.key);
                            }
                          };

                          return (
                            <button
                              key={col.key}
                              onClick={canClick ? handleColumnClick : undefined}
                              disabled={!canClick}
                              className={`w-full flex items-center gap-1.5 px-1.5 py-[3px] text-xs rounded transition-colors text-left group ${
                                isColSelected
                                  ? 'bg-primary-50 text-primary-700'
                                  : canClick
                                    ? 'text-gray-600 hover:text-gray-700 hover:bg-gray-100 cursor-pointer'
                                    : 'text-gray-600 cursor-default'
                              }`}
                              title={`${col.label} (${col.type})${col.aggregatable ? ' \u2014 aggregatable' : ''}${col.filterable === false ? ' \u2014 not filterable' : ''}`}
                            >
                              {/* Checkbox / add indicator */}
                              {canClick && (
                                <span className={`w-3 h-3 rounded border flex items-center justify-center flex-shrink-0 text-[8px] ${
                                  isColSelected
                                    ? 'bg-primary-600 border-primary-600 text-white'
                                    : 'border-gray-300 group-hover:border-gray-400'
                                }`}>
                                  {isColSelected && <CheckIcon size={9} />}
                                </span>
                              )}
                              <span className={`font-mono text-[10px] w-4 text-center flex-shrink-0 ${icon.color}`}>
                                {icon.symbol}
                              </span>
                              <span className="truncate flex-1">{col.label}</span>
                              {col.aggregatable && (
                                <span className="text-[9px] text-green-500 flex-shrink-0" title="Aggregatable">&Sigma;</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
